import { Spinner } from "flowbite-react"
import { useFormikContext } from "formik"
import { get } from "lodash"
import React from "react"
import Select, { MenuListProps, Props, components } from "react-select"

import { useMediaQuery } from "@hooks/useMediaQuery"
import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"

interface MonoSelectProps extends Props {
  size?: "xs" | "sm" | "md"
  name: string
  showDropdownIcon?: boolean
  hasError?: boolean
  variant?: "rounded" | "solid"
  onValueChange?: (newValue: SelectOptionType) => void
  underLineSelect?: boolean
  width?: boolean
  isFromWidget?: boolean
  prefixText?: string
}

interface MultiOptions {
  options: SelectOptionType[]
}

const MonoSelect = ({
  options,
  onValueChange,
  size = "sm",
  name,
  showDropdownIcon = true,
  menuPosition = "absolute",
  menuPlacement = "auto",
  underLineSelect,
  width = false,
  isLoading,
  isFromWidget = false,
  prefixText = "",
  ...rest
}: MonoSelectProps) => {
  const { errors, touched, setFieldValue, values } = useFormikContext()
  const error = get(errors, name)
  const value = get(values, name)
  const hasOptions = options && (options as unknown as MultiOptions[])[0]?.options?.length > 0
  let multiOpt

  if (hasOptions) {
    multiOpt = (options as unknown as any)
      .map((item: MultiOptions) => {
        return item?.options
      })
      .flat()
  } else {
    multiOpt = options
  }
  const fieldTouched = get(touched, name)
  const isMobile = useMediaQuery(768)

  const SingleValue = ({ children, ...props }: any) => {
    return (
      <components.SingleValue {...props}>
        {prefixText && `${prefixText}: `}
        {children}
      </components.SingleValue>
    )
  }

  return (
    <Select
      {...rest}
      isLoading={isLoading}
      menuPosition={menuPosition}
      menuPlacement={menuPlacement}
      name={name}
      options={multiOpt}
      classNamePrefix={"mono-select"}
      closeMenuOnScroll={(e: Event) =>
        isMobile &&
        e?.target &&
        !(e?.target as HTMLElement)?.classList?.contains("mono-select__menu-list")
      }
      onChange={(val) => {
        setFieldValue(name, (val as SelectOptionType)?.value)
        onValueChange?.(val as SelectOptionType)
      }}
      isOptionDisabled={(option) => (option as unknown as Record<string, boolean>).isDisable}
      // filterOption={option => option.value !== ''}
      value={
        multiOpt?.filter(
          (option: SelectOptionType[]) =>
            (option as unknown as SelectOptionType)?.value ===
            (typeof value === "object" ? value?.value : value)
        )[0] || null
      }
      components={{
        SingleValue,
        MenuList: (props: MenuListProps) =>
          isLoading ? (
            <Spinner size={"md"} className={"w-full mb-1"} />
          ) : (
            <components.MenuList {...props}>{props.children}</components.MenuList>
          ),
        ...(rest.components ?? {}),
      }}
      styles={{
        container: (baseStyles) => ({
          ...baseStyles,
          width: width ? "" : "100%",
        }),
        control: (baseStyles, state) => ({
          ...baseStyles,
          background: underLineSelect ? "#F9FAFB" : "transparent",
          height: size === "xs" ? "38px" : size === "sm" ? "40px" : "52px",
          borderWidth: underLineSelect ? "0px 0px 1px" : "1px",
          borderRadius: underLineSelect ? "unset" : "6px",
          boxShadow: underLineSelect ? "unset" : "",
          borderColor:
            error && fieldTouched
              ? "#DC3838"
              : state.isFocused
                ? underLineSelect
                  ? "transparent"
                  : "#2F69FE"
                : isFromWidget
                  ? "#CCCCCC"
                  : "#E7E7E8",
          "&:hover": {
            borderColor: error && fieldTouched ? "#DC3838" : "#89B8FF",
          },
        }),
        dropdownIndicator: (baseStyles) => ({
          ...baseStyles,
          display: showDropdownIcon ? "block" : "none",
          padding: 0,
          fontSize: 0,
          paddingRight: size === "md" ? "16px" : "12px",
        }),
        clearIndicator: (baseStyles) => ({
          ...baseStyles,
          cursor: "pointer",
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          paddingLeft: "14px",
          paddingRight: 0,
        }),
        singleValue: (baseStyles) => ({
          ...baseStyles,
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "120%",
          color: "#26272E",
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "120%",
          color: "#898A93",
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          boxShadow: "none",
          input: {
            boxShadow: "none !important",
          },
          fontSize: "14px",
        }),
        menuPortal: (baseStyles) => ({
          ...baseStyles,
          zIndex: 10,
        }),
        menuList: (base) => ({
          ...base,
          "::-webkit-scrollbar": {
            width: "3px",
            borderRadius: "6px",
          },
          "::-webkit-scrollbar-track": {
            background: "#E7E7E8",
          },
          "::-webkit-scrollbar-thumb": {
            background: "#CCCDD0",
            borderRadius: "6px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          marginTop: "2px",
          padding: "16px 8px 8px 8px",
        }),
        option: (baseStyles, state) => ({
          ...baseStyles,
          padding: "8px",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "120%",
          background: state.isSelected ? "#2F69FE" : state.isFocused ? "#F2F6FF" : "#FFFFFF",
          "&:hover": {
            background: state.isSelected ? "#2F69FE" : "#F2F6FF",
          },
          textTransform: "capitalize",
          cursor: "pointer",
        }),
      }}
    />
  )
}

export default MonoSelect
